import React, { useState, useEffect, useCallback } from 'react';
import { auditCashCut, downloadCashCutAuditExcel } from '../services/cashCutsApi';
import { toast } from 'sonner';
import {
    X, SpinnerGap, Money, CreditCard, Gift, ArrowsLeftRight, QrCode, ListMagnifyingGlass,
    MicrosoftExcelLogo, Printer,
} from '@phosphor-icons/react';

const fmt = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(n) || 0);
const formatDateTime = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('es-MX', {
            timeZone: 'America/Mexico_City',
            day: '2-digit', month: '2-digit', year: '2-digit',
            hour: '2-digit', minute: '2-digit', hour12: false,
        });
    } catch { return iso; }
};
const formatDayLabel = (dateStr) => {
    if (!dateStr || dateStr === 'sin-fecha') return 'Sin fecha';
    try {
        // dateStr is YYYY-MM-DD in local time — parse as local, format long
        const [y, m, d] = dateStr.split('-').map(Number);
        const dt = new Date(y, (m || 1) - 1, d || 1);
        return dt.toLocaleDateString('es-MX', {
            weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
        });
    } catch { return dateStr; }
};

const METHOD_ICON = {
    cash: Money,
    credit_card: CreditCard,
    debit_card: CreditCard,
    credit: CreditCard,
    debit: CreditCard,
    courtesy: Gift,
    transfer: ArrowsLeftRight,
    qr: QrCode,
    mixed: ListMagnifyingGlass,
};

const METHOD_COLOR = {
    cash: 'bg-[#ECFDF5] text-[#065F46] border-[#059669]/30',
    credit_card: 'bg-[#EEF6FF] text-[#1E40AF] border-[#3B82F6]/30',
    debit_card: 'bg-[#FDF4FF] text-[#701A75] border-[#A855F7]/30',
    credit: 'bg-[#EEF6FF] text-[#1E40AF] border-[#3B82F6]/30',
    debit: 'bg-[#FDF4FF] text-[#701A75] border-[#A855F7]/30',
    courtesy: 'bg-[#FDF5F5] text-[#B91C1C] border-[#FB7185]/30',
    transfer: 'bg-[#FEF3C7] text-[#92400E] border-[#F59E0B]/30',
    qr: 'bg-[#F5F3FF] text-[#5B21B6] border-[#8B5CF6]/30',
    mixed: 'bg-[#F1F5F9] text-[#1F2D4B] border-[#64748B]/30',
};

/**
 * Modal that displays an audit of a closed cash cut: certificates grouped by
 * payment method, showing folio, amount, TPV and operation #. Admin-only.
 */
const CashCutAuditModal = ({ cutId, open, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [exporting, setExporting] = useState(false);

    const handleExportExcel = async () => {
        if (!cutId) return;
        setExporting(true);
        try {
            await downloadCashCutAuditExcel(cutId, data?.cut?.folio);
            toast.success('Excel descargado');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'No se pudo exportar Excel');
        } finally {
            setExporting(false);
        }
    };

    const load = useCallback(async () => {
        if (!cutId) return;
        setLoading(true);
        try {
            const res = await auditCashCut(cutId);
            setData(res);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'No se pudo cargar la auditoría');
        } finally {
            setLoading(false);
        }
    }, [cutId]);

    useEffect(() => {
        if (open) load();
        else setData(null);
    }, [open, load]);

    if (!open) return null;

    const cut = data?.cut;

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-[#1F2D4B]/70 backdrop-blur-sm p-2 sm:p-4 audit-print-root"
            onClick={onClose}
            data-testid="cash-cut-audit-modal"
        >
            <style>{`
                @media print {
                    body * { visibility: hidden !important; }
                    .audit-print-root, .audit-print-root * { visibility: visible !important; }
                    .audit-print-root {
                        position: absolute !important; inset: 0 !important;
                        background: white !important; backdrop-filter: none !important;
                        padding: 0 !important; z-index: auto !important;
                    }
                    .audit-print-root .audit-print-body {
                        box-shadow: none !important; border: none !important;
                        max-height: none !important; overflow: visible !important;
                        width: 100% !important; max-width: none !important;
                        border-radius: 0 !important;
                    }
                    .audit-print-root .no-print { display: none !important; }
                    .audit-print-root table { page-break-inside: auto; }
                    .audit-print-root tr { page-break-inside: avoid; page-break-after: auto; }
                }
            `}</style>
            <div
                className="bg-white rounded-2xl shadow-2xl border border-[#ECE3D2] w-full max-w-5xl max-h-[92vh] flex flex-col audit-print-body"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-start justify-between gap-4 p-5 border-b border-[#ECE3D2] bg-[#F8F4EC] rounded-t-2xl">
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                            <ListMagnifyingGlass size={20} weight="duotone" className="text-[#FB7185]" />
                            <h2 className="font-display text-lg font-bold text-[#1F2D4B]">
                                Auditoría de corte
                            </h2>
                            <span className="text-[10px] uppercase tracking-wider bg-[#FB7185]/10 text-[#FB7185] px-2 py-0.5 rounded-full font-semibold">
                                Admin
                            </span>
                        </div>
                        {cut && (
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#4B5878]">
                                <span className="font-mono font-semibold text-[#1F2D4B]" data-testid="audit-folio">{cut.folio}</span>
                                <span>{cut.seller_name}</span>
                                <span>{formatDateTime(cut.created_at)}</span>
                                {cut.is_partial && (
                                    <span className="text-[10px] uppercase tracking-wider bg-[#F59E0B]/15 text-[#92400E] px-1.5 py-0.5 rounded font-semibold">
                                        Parcial · hasta {cut.until_date}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={() => window.print()}
                        disabled={loading || !data || (data?.days || []).length === 0}
                        data-testid="audit-print-btn"
                        className="mr-2 flex-shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-[#1F2D4B] text-white text-xs font-semibold hover:bg-[#0F1B3D] disabled:opacity-50 no-print"
                        title="Imprimir auditoría"
                    >
                        <Printer size={13} weight="bold" />
                        Imprimir
                    </button>
                    <button
                        type="button"
                        onClick={handleExportExcel}
                        disabled={loading || exporting || !data || (data?.groups || []).length === 0}
                        data-testid="audit-export-excel-btn"
                        className="mr-2 flex-shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-[#0F7A3E] text-white text-xs font-semibold hover:bg-[#0A5F30] disabled:opacity-50"
                        title="Exportar auditoría a Excel (una hoja por forma de pago)"
                    >
                        {exporting
                            ? <SpinnerGap size={13} className="animate-spin" />
                            : <MicrosoftExcelLogo size={13} weight="bold" />}
                        Excel
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="audit-close-btn"
                        className="text-[#8B8474] hover:text-[#1F2D4B] p-1 flex-shrink-0 no-print"
                    >
                        <X size={20} weight="bold" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                    {loading ? (
                        <div className="flex items-center justify-center py-16 text-[#8B8474]">
                            <SpinnerGap size={22} className="animate-spin mr-2" /> Cargando…
                        </div>
                    ) : !data || !data.days?.length ? (
                        <div className="py-12 text-center text-[#8B8474]" data-testid="audit-empty">
                            Este corte no tiene ventas asociadas.
                        </div>
                    ) : (
                        <>
                            {/* Summary strip (por forma de pago, se mantiene arriba) */}
                            <div className="flex flex-wrap gap-2" data-testid="audit-summary">
                                {data.groups.map((g) => {
                                    const Icon = METHOD_ICON[g.method] || ListMagnifyingGlass;
                                    const cls = METHOD_COLOR[g.method] || 'bg-[#F8F4EC] text-[#4B5878] border-[#ECE3D2]';
                                    return (
                                        <div
                                            key={g.method}
                                            className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${cls}`}
                                            data-testid={`audit-group-summary-${g.method}`}
                                        >
                                            <Icon size={16} weight="duotone" />
                                            <div>
                                                <p className="text-[10px] uppercase tracking-wide font-semibold">{g.label}</p>
                                                <p className="text-sm font-mono font-bold">{fmt(g.total)} <span className="text-[10px] font-normal opacity-75">({g.count})</span></p>
                                            </div>
                                        </div>
                                    );
                                })}
                                <div className="ml-auto flex items-center gap-2 px-3 py-2 rounded-lg border border-[#1F2D4B] bg-[#1F2D4B] text-white">
                                    <div>
                                        <p className="text-[10px] uppercase tracking-wide font-semibold opacity-80">Gran total</p>
                                        <p className="text-sm font-mono font-bold" data-testid="audit-grand-total">{fmt(data.grand_total)}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Bloques por día */}
                            {data.days.map((day) => (
                                <div
                                    key={day.date}
                                    className="border border-[#ECE3D2] rounded-xl overflow-hidden"
                                    data-testid={`audit-day-${day.date}`}
                                >
                                    <div className="px-4 py-2.5 bg-[#F8F4EC] border-b border-[#ECE3D2] flex items-center justify-between">
                                        <h3 className="text-sm font-semibold text-[#1F2D4B]">
                                            {formatDayLabel(day.date)}
                                            <span className="ml-2 text-[11px] font-mono text-[#8B8474]">({day.count})</span>
                                        </h3>
                                        <span className="text-sm font-mono font-bold text-[#1F2D4B]">{fmt(day.total)}</span>
                                    </div>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-xs">
                                            <thead>
                                                <tr className="bg-white border-b border-[#ECE3D2] text-[10px] uppercase tracking-wide text-[#8B8474]">
                                                    <th className="text-left px-3 py-2 font-semibold">Fecha</th>
                                                    <th className="text-left px-3 py-2 font-semibold">Folio(s)</th>
                                                    <th className="text-left px-3 py-2 font-semibold">Cliente</th>
                                                    <th className="text-left px-3 py-2 font-semibold">Forma de pago</th>
                                                    <th className="text-left px-3 py-2 font-semibold">Tarjeta</th>
                                                    <th className="text-left px-3 py-2 font-semibold">TPV</th>
                                                    <th className="text-left px-3 py-2 font-semibold">Nº Op.</th>
                                                    <th className="text-right px-3 py-2 font-semibold">Monto</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {day.rows.map((r, idx) => (
                                                    <tr
                                                        key={`${r.sale_id}-${r.method}-${idx}`}
                                                        className="border-b border-[#ECE3D2] last:border-b-0 hover:bg-[#FAF6F0]"
                                                        data-testid={`audit-day-row-${day.date}-${idx}`}
                                                    >
                                                        <td className="px-3 py-2 font-mono text-[#4B5878] whitespace-nowrap">{formatDateTime(r.created_at)}</td>
                                                        <td className="px-3 py-2 font-mono font-semibold text-[#1F2D4B]">
                                                            {r.folio}
                                                            {r.is_mixed_leg && (
                                                                <span className="ml-1.5 text-[9px] bg-[#FB7185]/15 text-[#FB7185] px-1.5 py-0.5 rounded font-bold uppercase">
                                                                    MX
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-2 text-[#4B5878]">{r.customer_name}</td>
                                                        <td className="px-3 py-2 text-[#4B5878]">{r.method_label}</td>
                                                        <td className="px-3 py-2 font-mono text-[#4B5878]">{r.card_last4 ? `••${r.card_last4}` : '—'}</td>
                                                        <td className="px-3 py-2 text-[#4B5878]">{r.tpv_name || '—'}</td>
                                                        <td className="px-3 py-2 font-mono text-[#4B5878]">{r.operation_number || '—'}</td>
                                                        <td className="px-3 py-2 text-right font-mono font-semibold text-[#1F2D4B]">{fmt(r.amount)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ))}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CashCutAuditModal;
